// src/modules/reports/teerab/Orders.js
import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  Title,
  useNotify,
} from "react-admin";
import {
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  MenuItem,
  Select,
  TextField as MuiTextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { alpha } from "@mui/material/styles";

// Reusable components from teerab folder
import {
  SummaryCard,
  SafePieChart,
  SafeBarChart,
  TopPerformersList,
  formatSDG,
} from "../../components/reports/teerab";

// List component for orders – corrected path
import { OrderList } from "../../Orders";

// Icons
import {
  Timeline as TimelineIcon,
  AttachMoney as RevenueIcon,
  Person as BuyerIcon,
  Store as SellerIcon,
} from "@mui/icons-material";

// Type-specific inner dashboard
const TypeOrdersDashboard = ({ type }) => {
  const notify = useNotify();
  const theme = useTheme();

  // Map tab values to API negotiation types
  const typeMap = {
    crops: "Crop",
    services: "Service",
    inputs: "Input",
  };
  const apiType = typeMap[type];

  // Filter states (excluding typeFilter)
  const [statusFilter, setStatusFilter] = useState("");
  const [buyerType, setBuyerType] = useState("");
  const [sellerType, setSellerType] = useState("");
  const [buyerState, setBuyerState] = useState("");
  const [sellerState, setSellerState] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Meta data
  const [states, setStates] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  // Report data
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Inner tab (Overview / List)
  const [innerTab, setInnerTab] = useState(0);

  const token = JSON.parse(localStorage.getItem("token"));

  // Fetch states (static data)
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const stateRes = await fetch(
          `${process.env.REACT_APP_URL}/admin/state`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        const statesData = await stateRes.json();
        setStates(statesData);
      } catch (err) {
        console.error("Error fetching states:", err);
        notify("Error loading states", { type: "warning" });
      } finally {
        setLoadingMeta(false);
      }
    };
    fetchStates();
  }, [notify]);

  // Fetch report data when filters change
  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();

      // Add type filter from prop
      if (apiType) params.append("type", apiType);

      if (statusFilter) params.append("status", statusFilter);
      if (buyerType) params.append("buyerType", buyerType);
      if (buyerState) params.append("buyerState", buyerState);
      if (sellerType) params.append("sellerType", sellerType);
      if (sellerState) params.append("sellerState", sellerState);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);

      const url =
        `${process.env.REACT_APP_URL}/admin/reports/orders` +
        (params.toString() ? `?${params.toString()}` : "");

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const responseData = await response.json();
        const data = responseData.data;

        const transformedData = {
          summary: data[0]?.summary || { totalOrders: 0 },
          statusDistribution: data[1]?.statusDistribution || [],
          timeSeries: data[1]?.timeSeries || [],
          topBuyers: data[2]?.topBuyers || [],
          topSellers: data[2]?.topSellers || [],
          orders: Array.isArray(data[3]) ? data[3] : [],
          revenueByType: data[4]?.revenueByType || [],
        };

        setReportData(transformedData);
      } catch (err) {
        console.error("Error fetching report data:", err);
        setError(err);
        notify("Error loading order report data", { type: "error" });
      } finally {
        setLoading(false);
      }
    };

    if (apiType) {
      fetchReport();
    }
  }, [
    apiType,
    statusFilter,
    buyerType,
    sellerType,
    buyerState,
    sellerState,
    startDate,
    endDate,
    token,
    notify,
  ]);

  // Options for dropdowns
  const allStatuses = [
    "Processing",
    "Delivered",
    "Cancelled",
    "Pending",
    "Paid",
    "Shipped",
  ];
  const userTypes = [
    { id: "farmer", name: "Farmer" },
    { id: "agriculture_cooperative", name: "Farmer Cooperative" },
    { id: "consumer_cooperative", name: "Consumer Cooperative" },
    { id: "supplier", name: "Supplier" },
    { id: "buyer", name: "Buyer" },
    { id: "marketer", name: "Marketer" },
  ];

  if (loadingMeta) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!apiType) {
    return <Alert severity="warning">Invalid order type</Alert>;
  }

  return (
    <Box>
      {/* Filter Bar */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">Status</Typography>
          <Select
            fullWidth
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {allStatuses.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">Buyer Type</Typography>
          <Select
            fullWidth
            value={buyerType}
            onChange={(e) => setBuyerType(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">Any</MenuItem>
            {userTypes.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">Seller Type</Typography>
          <Select
            fullWidth
            value={sellerType}
            onChange={(e) => setSellerType(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">Any</MenuItem>
            {userTypes.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">Buyer State</Typography>
          <Select
            fullWidth
            value={buyerState}
            onChange={(e) => setBuyerState(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {states.map((state) => (
              <MenuItem key={state.id} value={state.id}>
                {state.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <Typography variant="subtitle2">Seller State</Typography>
          <Select
            fullWidth
            value={sellerState}
            onChange={(e) => setSellerState(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {states.map((state) => (
              <MenuItem key={state.id} value={state.id}>
                {state.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <DatePicker
            label="From"
            value={startDate || null}
            onChange={(newDate) => {
              const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
              setStartDate(iso);
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                fullWidth
                size="small"
                inputProps={{ ...params.inputProps, readOnly: true }}
              />
            )}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <DatePicker
            label="To"
            value={endDate || null}
            onChange={(newDate) => {
              const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
              setEndDate(iso);
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                fullWidth
                size="small"
                inputProps={{ ...params.inputProps, readOnly: true }}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Inner Tabs (Overview / List) */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={innerTab}
            onChange={(e, newValue) => setInnerTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontWeight: "600",
                textTransform: "none",
                fontSize: "0.875rem",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Order List" />
          </Tabs>
        </Paper>
      </Box>

      {/* Loading / Error / Content */}
      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ my: 3 }}>
          Failed to load data. {error.message}
        </Alert>
      )}

      {!loading && !error && reportData && innerTab === 0 && (
        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12} md={4}>
            <SummaryCard
              title="Total Orders"
              value={reportData.summary.totalOrders}
              icon={TimelineIcon}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SummaryCard
              title={`Revenue (${type})`}
              value={formatSDG(
                reportData.revenueByType.find((r) => r.type === apiType)?.totalRevenue || 0
              )}
              icon={RevenueIcon}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SummaryCard
              title="Avg. Order Value"
              value={formatSDG(
                reportData.summary.totalOrders
                  ? (reportData.revenueByType.find((r) => r.type === apiType)?.totalRevenue || 0) /
                    reportData.summary.totalOrders
                  : 0
              )}
              icon={RevenueIcon}
            />
          </Grid>

          {/* Charts */}
          <Grid item xs={12} md={6}>
            <SafePieChart
              data={reportData.statusDistribution}
              title="Order Status Distribution"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SafeBarChart
              data={reportData.timeSeries}
              title="Monthly Order Trends"
              xKey="month"
              yKey="count"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TopPerformersList
              title="Top Buyers"
              data={reportData.topBuyers}
              icon={BuyerIcon}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TopPerformersList
              title="Top Sellers"
              data={reportData.topSellers}
              icon={SellerIcon}
            />
          </Grid>
        </Grid>
      )}

      {!loading && !error && innerTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Pass type filter to OrderList */}
            <OrderList
              resource="orders"
              filter={{ type: apiType }}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

// Main component with outer tabs
const Orders = () => {
  const theme = useTheme();
  const [outerTab, setOuterTab] = useState(0); // 0: Crops, 1: Services, 2: Inputs

  const outerTabs = [
    { label: "Crops", value: "crops" },
    { label: "Services", value: "services" },
    { label: "Inputs", value: "inputs" },
  ];

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.default,
          0.9
        )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        p: 3,
        borderRadius: "16px",
      }}
    >
      <Title title="Orders Dashboard" />

      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight="800"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          Orders Dashboard
        </Typography>
      </Box>

      {/* Outer Tabs */}
      <Box sx={{ mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={outerTab}
            onChange={(e, newValue) => setOuterTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                fontWeight: "600",
                textTransform: "none",
                fontSize: "1rem",
              },
            }}
          >
            {outerTabs.map((tab, index) => (
              <Tab key={tab.value} label={tab.label} />
            ))}
          </Tabs>
        </Paper>
      </Box>

      {/* Render active type dashboard */}
      <TypeOrdersDashboard type={outerTabs[outerTab].value} />
    </Box>
  );
};

export default Orders;